import React, { useCallback, useEffect, useMemo, useState } from "react";
import cartService from "../../services/cart.service";
import { getCartDrawerFlowCss } from "../../utils/sectionBlockCss";
import { STATIC_CART_SECTION_INDEX } from "../../constants/custom-cart-drawer-template";
import {
  evaluateCartDiscountApplyResult,
  getPrimaryCartDiscountCode,
  isCartDiscountCodeApplied,
} from "../../../shared/cart-discount-codes.js";
import {
  EDITOR_FALLBACK_CART_COUPONS,
  estimateCartCouponSavingsCents,
  isLikelyShopifyShopDomain,
  normalizeCartCouponRecord,
  resolveVisibleCartCoupons,
  shouldFetchCartCouponSuggestions,
} from "../../../shared/cart-discount-coupon-offer.js";
import CartCouponSuggestionList from "./CartCouponSuggestionList";
import CartSaveMoreSummaryRow from "./CartSaveMoreSummaryRow";
import { useApp } from "../../context/AppState";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

function getSaveButtonStyle(section, isMobile) {
  const saveCss = section?.setting?.saveButtonCustomCSS;
  if (!saveCss) return {};
  return getCartDrawerFlowCss({ setting: { customCSS: saveCss } }, isMobile);
}

async function refreshCartSnapshot(fallbackCart) {
  try {
    return await cartService.fetchCart();
  } catch (fetchErr) {
    console.warn("[CartDrawerFooterActions] cart refresh failed:", fetchErr);
    return fallbackCart;
  }
}

/** Editor-only mock apply so bill summary / totals update in preview. */
function applyEditorPreviewDiscount(cart, code, coupon) {
  const base = cart && typeof cart === "object" ? cart : {};
  const itemsSubtotal =
    Number(base.items_subtotal_price) ||
    Number(base.total_price) ||
    0;
  const estimated = coupon
    ? estimateCartCouponSavingsCents(base, coupon)
    : 0;
  const allocated = Math.max(
    0,
    estimated || Math.round(itemsSubtotal * 0.1)
  );
  const nonCodeApps = (base.cart_level_discount_applications || []).filter(
    (app) => String(app?.type || "").toLowerCase() !== "discount_code"
  );
  const nonCodeDiscount = nonCodeApps.reduce(
    (sum, app) => sum + (Number(app?.total_allocated_amount) || 0),
    0
  );
  const nextTotalDiscount = nonCodeDiscount + allocated;
  const nextTotalPrice = Math.max(0, itemsSubtotal - allocated);

  return {
    ...base,
    total_discount: nextTotalDiscount,
    total_price: nextTotalPrice,
    items_subtotal_price: itemsSubtotal,
    cart_level_discount_applications: [
      ...nonCodeApps,
      {
        type: "discount_code",
        title: coupon?.summary || coupon?.title || code,
        value: String(code),
        value_type: "fixed_amount",
        total_allocated_amount: allocated,
      },
    ],
    discount_codes: [{ code, applicable: true }],
  };
}

function CartDrawerBottomSheet({
  open,
  title,
  subtitle,
  onClose,
  onSave,
  saving,
  error,
  saveLabel,
  saveButtonStyle,
  embeddedInEditor,
  hideFooter = false,
  headerClassName = "",
  panelClassName = "",
  children,
}) {
  useEffect(() => {
    if (!open || embeddedInEditor) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [embeddedInEditor, onClose, open]);

  if (!open) return null;

  return (
    <div className="weu-custom-cart-drawer__sheet-root" role="presentation">
      <button
        type="button"
        className="weu-custom-cart-drawer__sheet-backdrop"
        onClick={embeddedInEditor ? undefined : onClose}
        aria-label="Close"
        tabIndex={embeddedInEditor ? -1 : 0}
      />
      <div
        className={`weu-custom-cart-drawer__sheet-panel${
          panelClassName ? ` ${panelClassName}` : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className={`weu-custom-cart-drawer__sheet-header${
            headerClassName ? ` ${headerClassName}` : ""
          }`}
        >
          <div className="weu-custom-cart-drawer__sheet-heading">
            <h3 className="weu-custom-cart-drawer__sheet-title">{title}</h3>
            {subtitle ? (
              <p className="weu-custom-cart-drawer__sheet-subtitle">{subtitle}</p>
            ) : null}
          </div>
          {headerClassName?.includes("save-more") ? (
            <div
              className="weu-custom-cart-drawer__sheet-header-art"
              aria-hidden
            >
              <span className="weu-custom-cart-drawer__sheet-header-art-bag">%</span>
            </div>
          ) : null}
          <button
            type="button"
            className="weu-custom-cart-drawer__sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="weu-custom-cart-drawer__sheet-body">{children}</div>
        {!hideFooter ? (
          <div className="weu-custom-cart-drawer__sheet-footer">
            <button
              type="button"
              className="weu-custom-cart-drawer__sheet-save"
              style={saveButtonStyle}
              onClick={onSave}
              disabled={embeddedInEditor || saving}
            >
              {saving ? "…" : saveLabel}
            </button>
          </div>
        ) : null}
        {error ? (
          <p className="weu-custom-cart-drawer__sheet-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function DiscountApplySuccess({ code, savedText, totalText }) {
  return (
    <div className="weu-custom-cart-drawer__discount-success" role="status">
      <div className="weu-custom-cart-drawer__discount-success-icon" aria-hidden>
        ✓
      </div>
      <span className="weu-custom-cart-drawer__discount-success-code">
        ✔ {code} applied
      </span>
      {totalText ? (
        <p className="weu-custom-cart-drawer__discount-success-total">{totalText}</p>
      ) : null}
      {savedText ? (
        <span className="weu-custom-cart-drawer__discount-success-saved">
          Saved {savedText}
        </span>
      ) : null}
    </div>
  );
}

function withTimeout(promise, ms = 8000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error("discount-codes timeout")), ms);
    }),
  ]);
}

export function useCartDrawerFooterActions({
  discountSection,
  orderNoteSection,
  showDiscountTrigger,
  showOrderNoteTrigger,
  cart,
  isMobile,
  embeddedInEditor,
  getEditorPartProps,
  onCartUpdated,
  onNoteChange,
  onDiscountApplied,
  formatMoney,
  discountCodeLabels = {},
}) {
  const { appState } = useApp();
  const shopDomain = String(
    appState?.shop?.shop ||
      (typeof window !== "undefined" && window?.Shopify?.shop) ||
      ""
  ).trim();

  const [activeSheet, setActiveSheet] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [note, setNote] = useState(cart?.note || "");
  const [saving, setSaving] = useState(false);
  const [applyingCode, setApplyingCode] = useState("");
  const [error, setError] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [applySuccess, setApplySuccess] = useState(null);

  const discountTriggerCss = useMemo(
    () => getCartDrawerFlowCss(discountSection, isMobile),
    [discountSection, isMobile]
  );
  const orderNoteTriggerCss = useMemo(
    () => getCartDrawerFlowCss(orderNoteSection, isMobile),
    [orderNoteSection, isMobile]
  );

  const discountTriggerLabel =
    discountSection?.text?.trim() || "Add discount code";
  const orderNoteTriggerLabel =
    orderNoteSection?.text?.trim() || "Add order note";
  const discountPlaceholder =
    discountSection?.setting?.inputPlaceholder?.trim() || "Enter coupon code";
  const discountCouponSetting = discountSection?.setting || {};
  const shouldShowCouponSuggestions = shouldFetchCartCouponSuggestions(
    discountCouponSetting
  );
  const couponSettingKey = useMemo(
    () =>
      JSON.stringify({
        show: discountCouponSetting?.showCouponSuggestions === true,
        all: discountCouponSetting?.showAllCouponCodes !== false,
        codes: discountCouponSetting?.selectedCouponCodes || [],
      }),
    [discountCouponSetting]
  );
  const orderNotePlaceholder =
    orderNoteSection?.setting?.inputPlaceholder?.trim() ||
    "Leave a note for seller";

  const discountEditorProps = getEditorPartProps?.(
    STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT,
    discountTriggerCss
  );
  const orderNoteEditorProps = getEditorPartProps?.(
    STATIC_CART_SECTION_INDEX.ORDER_NOTE,
    orderNoteTriggerCss
  );

  useEffect(() => {
    setNote(cart?.note || "");
  }, [cart?.note]);

  useEffect(() => {
    if (!shouldShowCouponSuggestions) {
      setAvailableCoupons([]);
      setLoadingCoupons(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingCoupons(true);

    const fallbackCoupons = EDITOR_FALLBACK_CART_COUPONS.map(
      normalizeCartCouponRecord
    ).filter(Boolean);

    const finish = (codes) => {
      if (cancelled) return;
      const normalized = (Array.isArray(codes) ? codes : [])
        .map(normalizeCartCouponRecord)
        .filter(Boolean);
      if (normalized.length) {
        setAvailableCoupons(normalized);
        return;
      }
      // Editor / local: show all must still render coupons.
      if (embeddedInEditor || !isLikelyShopifyShopDomain(shopDomain)) {
        setAvailableCoupons(fallbackCoupons);
      } else {
        setAvailableCoupons([]);
      }
    };

    const load = async () => {
      try {
        if (!isLikelyShopifyShopDomain(shopDomain)) {
          finish(fallbackCoupons);
          return;
        }
        const codes = await withTimeout(
          cartService.fetchStoreDiscountCodes(shopDomain),
          8000
        );
        finish(codes);
      } catch (err) {
        console.warn("[CartDrawerFooterActions] Failed to load coupons:", err);
        finish(embeddedInEditor ? fallbackCoupons : []);
      } finally {
        if (!cancelled) setLoadingCoupons(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    couponSettingKey,
    embeddedInEditor,
    shopDomain,
    shouldShowCouponSuggestions,
  ]);

  const visibleCoupons = useMemo(
    () => resolveVisibleCartCoupons(discountCouponSetting, availableCoupons),
    [availableCoupons, discountCouponSetting]
  );

  useEffect(() => {
    const applied = getPrimaryCartDiscountCode(cart);
    if (!applied || !availableCoupons.length) return;
    if (!isCartDiscountCodeApplied(cart, applied)) return;

    const matched = availableCoupons.find(
      (coupon) =>
        String(coupon.code).trim().toLowerCase() ===
        String(applied).trim().toLowerCase()
    );
    if (!matched) return;

    onDiscountApplied?.({
      code: applied,
      title: matched.summary || matched.title || applied,
    });
  }, [availableCoupons, cart, onDiscountApplied]);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
    setError("");
    setSaving(false);
    setApplyingCode("");
    setApplySuccess(null);
  }, []);

  const applyDiscountCode = useCallback(
    async (rawCode) => {
      const trimmed = String(rawCode || "").trim();
      if (!trimmed || saving) return false;

      if (embeddedInEditor) {
        const normalizedCode = trimmed.toLowerCase();
        const matchedCoupon = availableCoupons.find(
          (coupon) =>
            String(coupon.code).trim().toLowerCase() === normalizedCode
        );
        const beforeDiscount = Number(cart?.total_discount) || 0;
        const nextCart = applyEditorPreviewDiscount(
          cart,
          trimmed,
          matchedCoupon
        );
        const afterDiscount = Number(nextCart?.total_discount) || 0;
        const savedCents = Math.max(0, afterDiscount - beforeDiscount) || afterDiscount;

        onDiscountApplied?.({
          code: trimmed,
          title:
            matchedCoupon?.summary ||
            matchedCoupon?.title ||
            trimmed,
        });
        onCartUpdated?.(nextCart);

        if (shouldShowCouponSuggestions) {
          setApplySuccess({
            code: trimmed.toUpperCase(),
            savedText:
              savedCents > 0 && typeof formatMoney === "function"
                ? formatMoney(savedCents)
                : "",
            totalText:
              typeof formatMoney === "function"
                ? formatMoney(Number(nextCart?.total_price) || 0)
                : "",
          });
          window.setTimeout(() => {
            closeSheet();
          }, 1400);
        } else {
          closeSheet();
        }
        return true;
      }

      setSaving(true);
      setApplyingCode(trimmed);
      setError("");
      setApplySuccess(null);
      try {
        const beforeCart = cart || (await cartService.fetchCart());
        const beforeDiscount = Number(beforeCart?.total_discount) || 0;
        const normalizedCode = trimmed.toLowerCase();

        if (isCartDiscountCodeApplied(beforeCart, normalizedCode)) {
          closeSheet();
          return true;
        }

        const existingCode = getPrimaryCartDiscountCode(beforeCart);
        const isSwitching =
          existingCode &&
          existingCode.toLowerCase() !== normalizedCode &&
          isCartDiscountCodeApplied(beforeCart, existingCode);

        if (isSwitching) {
          await cartService.clearCartDiscount();
        }

        const updated = await cartService.updateCart({ discount: trimmed });
        if (!updated) {
          if (isSwitching && existingCode) {
            const restored = await cartService.updateCart({
              discount: existingCode,
            });
            onCartUpdated?.(await refreshCartSnapshot(restored || beforeCart));
          }
          setError("Discount code could not be applied.");
          return false;
        }

        const applyResult = evaluateCartDiscountApplyResult(
          beforeCart,
          updated,
          trimmed
        );
        if (!applyResult.success) {
          if (isSwitching && existingCode) {
            const restored = await cartService.updateCart({
              discount: existingCode,
            });
            onCartUpdated?.(await refreshCartSnapshot(restored || beforeCart));
          } else {
            await cartService.clearCartDiscount();
            onCartUpdated?.(await refreshCartSnapshot(beforeCart));
          }
          setError(applyResult.message);
          return false;
        }

        const freshCart = await refreshCartSnapshot(updated);
        const afterDiscount = Number(freshCart?.total_discount) || 0;
        const savedCents = Math.max(0, afterDiscount - beforeDiscount) || afterDiscount;

        const matchedCoupon = availableCoupons.find(
          (coupon) =>
            String(coupon.code).trim().toLowerCase() === normalizedCode
        );
        onDiscountApplied?.({
          code: trimmed,
          title:
            matchedCoupon?.summary ||
            matchedCoupon?.title ||
            trimmed,
        });
        onCartUpdated?.(freshCart);

        if (shouldShowCouponSuggestions) {
          setApplySuccess({
            code: trimmed.toUpperCase(),
            savedText:
              savedCents > 0 && typeof formatMoney === "function"
                ? formatMoney(savedCents)
                : "",
            totalText:
              typeof formatMoney === "function"
                ? formatMoney(Number(freshCart?.total_price) || 0)
                : "",
          });
          window.setTimeout(() => {
            closeSheet();
          }, 1400);
        } else {
          closeSheet();
        }
        return true;
      } catch (err) {
        console.error("[CartDrawerFooterActions] discount apply failed:", err);
        setError("Discount code could not be applied.");
        return false;
      } finally {
        setSaving(false);
        setApplyingCode("");
      }
    },
    [
      availableCoupons,
      cart,
      closeSheet,
      embeddedInEditor,
      formatMoney,
      onCartUpdated,
      onDiscountApplied,
      saving,
      shouldShowCouponSuggestions,
    ]
  );

  const appliedCartCode = useMemo(
    () => getPrimaryCartDiscountCode(cart),
    [cart]
  );

  const openSheet = useCallback(
    (type) => {
      setActiveSheet(type);
      setError("");
      setApplySuccess(null);
      if (type === "discount" && !embeddedInEditor) {
        setDiscountCode(appliedCartCode || "");
      }
    },
    [appliedCartCode, embeddedInEditor]
  );

  const handleSaveDiscount = useCallback(async () => {
    await applyDiscountCode(discountCode);
  }, [applyDiscountCode, discountCode]);

  const handleSaveNote = useCallback(async () => {
    if (embeddedInEditor) return;
    if (saving) return;

    setSaving(true);
    setError("");
    try {
      const nextNote = String(note || "");
      const updated = await cartService.updateCart({ note: nextNote });
      if (updated) {
        onCartUpdated?.(updated);
        onNoteChange?.(nextNote);
      }
      closeSheet();
    } catch (err) {
      console.error("[CartDrawerFooterActions] note save failed:", err);
      setError("Could not save order note.");
    } finally {
      setSaving(false);
    }
  }, [closeSheet, embeddedInEditor, note, onCartUpdated, onNoteChange, saving]);

  const hasTriggers = showDiscountTrigger || showOrderNoteTrigger;
  const discountSheetTitle = shouldShowCouponSuggestions
    ? "Save more"
    : discountTriggerLabel;
  const discountSheetSubtitle = shouldShowCouponSuggestions
    ? "with coupons and offers"
    : "";

  const triggers = !hasTriggers ? null : (
    <div className="weu-custom-cart-drawer__footer-actions">
      {showOrderNoteTrigger ? (
        <button
          type="button"
          className="weu-custom-cart-drawer__footer-action weu-custom-cart-drawer__part"
          style={orderNoteTriggerCss}
          {...orderNoteEditorProps}
          onClick={(event) => {
            orderNoteEditorProps?.onClick?.(event);
            openSheet("note");
          }}
          onKeyDown={(event) => {
            orderNoteEditorProps?.onKeyDown?.(event);
          }}
        >
          {orderNoteTriggerLabel}
        </button>
      ) : null}
      {showDiscountTrigger ? (
        shouldShowCouponSuggestions ? (
          <CartSaveMoreSummaryRow
            cart={cart}
            formatMoney={formatMoney}
            couponCount={visibleCoupons.length}
            discountCodeLabels={discountCodeLabels}
            onOpen={() => openSheet("discount")}
            disabled={false}
            editorPartProps={
              // Keep editor selection handlers; omit text-link trigger CSS
              // so the premium Save more card styles can apply.
              (({ style, ...rest }) => rest)(discountEditorProps || {})
            }
          />
        ) : (
          <button
            type="button"
            className="weu-custom-cart-drawer__footer-action weu-custom-cart-drawer__part"
            style={discountTriggerCss}
            {...discountEditorProps}
            onClick={(event) => {
              discountEditorProps?.onClick?.(event);
              openSheet("discount");
            }}
            onKeyDown={(event) => {
              discountEditorProps?.onKeyDown?.(event);
            }}
          >
            {discountTriggerLabel}
          </button>
        )
      ) : null}
    </div>
  );

  const sheets = !hasTriggers ? null : (
    <>
      <CartDrawerBottomSheet
        open={activeSheet === "note"}
        title={orderNoteTriggerLabel}
        onClose={closeSheet}
        onSave={handleSaveNote}
        saving={saving}
        error={error}
        saveLabel={orderNoteSection?.setting?.saveButtonLabel?.trim() || "Save"}
        saveButtonStyle={getSaveButtonStyle(orderNoteSection, isMobile)}
        embeddedInEditor={embeddedInEditor}
      >
        <textarea
          className="weu-custom-cart-drawer__sheet-textarea"
          placeholder={orderNotePlaceholder}
          value={note}
          onChange={(event) => {
            const next = event.target.value;
            setNote(next);
            onNoteChange?.(next);
          }}
          rows={4}
          disabled={embeddedInEditor || saving}
          aria-label={orderNotePlaceholder}
        />
      </CartDrawerBottomSheet>

      <CartDrawerBottomSheet
        open={activeSheet === "discount"}
        title={discountSheetTitle}
        subtitle={discountSheetSubtitle}
        onClose={closeSheet}
        onSave={handleSaveDiscount}
        saving={saving}
        error={error}
        saveLabel={
          shouldShowCouponSuggestions
            ? "Apply"
            : discountSection?.setting?.saveButtonLabel?.trim() || "Save"
        }
        saveButtonStyle={getSaveButtonStyle(discountSection, isMobile)}
        embeddedInEditor={embeddedInEditor}
        hideFooter={shouldShowCouponSuggestions}
        panelClassName={
          shouldShowCouponSuggestions
            ? "weu-custom-cart-drawer__sheet-panel--save-more"
            : ""
        }
        headerClassName={
          shouldShowCouponSuggestions
            ? "weu-custom-cart-drawer__sheet-header--save-more"
            : ""
        }
      >
        {applySuccess ? (
          <DiscountApplySuccess
            code={applySuccess.code}
            savedText={applySuccess.savedText}
            totalText={applySuccess.totalText}
          />
        ) : (
          <>
            <div
              className={`weu-custom-cart-drawer__coupon-input-row${
                shouldShowCouponSuggestions
                  ? " weu-custom-cart-drawer__coupon-input-row--inline-apply"
                  : ""
              }`}
            >
              <input
                type="text"
                className="weu-custom-cart-drawer__sheet-input"
                placeholder={discountPlaceholder}
                value={discountCode}
                onChange={(event) => setDiscountCode(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSaveDiscount();
                  }
                }}
                disabled={saving}
                aria-label={discountPlaceholder}
              />
              {shouldShowCouponSuggestions ? (
                <button
                  type="button"
                  className="weu-custom-cart-drawer__coupon-input-apply"
                  onClick={handleSaveDiscount}
                  disabled={saving || !String(discountCode || "").trim()}
                >
                  {saving && !applyingCode ? "…" : "Apply"}
                </button>
              ) : null}
            </div>

            <CartCouponSuggestionList
              coupons={visibleCoupons}
              cart={cart}
              formatMoney={formatMoney}
              selectedCode={discountCode}
              onSelect={setDiscountCode}
              onApply={applyDiscountCode}
              disabled={saving}
              applyingCode={applyingCode}
              isApplied={(code) => isCartDiscountCodeApplied(cart, code)}
            />
            {visibleCoupons.length === 0 &&
            loadingCoupons &&
            shouldShowCouponSuggestions ? (
              <p className="weu-custom-cart-drawer__coupon-list-label">
                Loading discounts…
              </p>
            ) : null}
            {visibleCoupons.length === 0 &&
            !loadingCoupons &&
            shouldShowCouponSuggestions ? (
              <p className="weu-custom-cart-drawer__coupon-list-label">
                No coupons available right now. You can still enter a code above.
              </p>
            ) : null}
          </>
        )}
      </CartDrawerBottomSheet>
    </>
  );

  return { triggers, sheets };
}

export default function CartDrawerFooterActions(props) {
  const { triggers, sheets } = useCartDrawerFooterActions(props);
  if (!triggers) return null;
  return (
    <>
      {triggers}
      {sheets}
    </>
  );
}
